import Colors from "../core/Colors";
import { ActorBreed } from "../core/actors/Actor";
import { priorityController } from "../core/actors/controllers/genericController";

const standardTriggers = {
  health: [["standardHealth"]],
  hunger: [["standardHunger"]],
};

const buildDefaultEnemy = ({
  initial,
  words,
  color = Colors.GRAY_3,
  hp = 1,
  triggers = standardTriggers,
}: {
  initial: string;
  words: string[];
  color?: string;
  hp?: number;
  triggers?: typeof standardTriggers;
}) => {
  if (words.length) {
    return {
      displayPriority: 100,
      glyph: {
        fg: color,
        bg: "transparent",
        ch: null,
      },
      initialPieces: initial,
      text: {
        name: "",
        description: "",
      },
      baseStats: {
        level: 1,
        health: hp,
        hunger: 500,
      },
      triggers: triggers,
      actionController: priorityController,
      aiTraits: ["randomMove"],
      aiWords: words,
    };
  }
};

const breeds = {
  letterPlacement: {
    displayPriority: 100,
    glyph: {
      fg: Colors.AQUA_2,
      bg: "transparent",
      ch: " ",
    },
  },
  genericBlock: {
    displayPriority: 100,
    glyph: {
      fg: Colors.GRAY_2,
      bg: "transparent",
      ch: " ",
    },
  },
  player: {
    displayPriority: 100,
    glyph: {
      fg: Colors.EGGSHELL,
      bg: "transparent",
      ch: "P",
    },
    initialPieces: "PLAYER",
    isCapital: true,
    baseStats: {
      level: 1,
      health: 3,
      hunger: 4,
      maxLetters: 12,
    },
    triggers: {
      health: [["playerHealth"]],
      hunger: [["standardHunger"]],
      playedWord: [
        ["wordRegeneration", { 3: 3, 4: 4, 5: 6, 6: 7, 7: 100 }],
        ["wordHunger", { 2: -1, 3: -1, 4: 0, 6: 1, 7: 100 }],
      ],
    },
    text: {
      name: "A Speller",
      description: "A wizardling looking for glory admist the spellgeon",
      logName: "You",
    },
    wielding: {},
    wearing: {},
    contains: [
      ["healingPotion", 1],
      ["staminaPotion", 1],
      ["potionLettersMinor", 1],
    ],
    spellBook: ["exchange"],
  },
  whelp: buildDefaultEnemy({ initial: "whelp", words: ["whelp"] }),
  rat: buildDefaultEnemy({ initial: "rat", words: ["tar", "rat"] }),
  aa: buildDefaultEnemy({ initial: "a", words: ["aa"] }),
  sprite: buildDefaultEnemy({
    initial: "sprite",
    words: ["spry", "springy", "sprite"],
  }),
  ape: buildDefaultEnemy({
    initial: "ape",
    words: ["ape", "banana"],
  }),
  wizard: buildDefaultEnemy({
    initial: "wizard",
    words: ["swizz", "zazzy", "wizard"],
  }),
  vixen: buildDefaultEnemy({
    initial: "vixen",
    words: ["vixen", "exiled", "view", "vieled"],
  }),
  onion: buildDefaultEnemy({
    initial: "onion",
    words: ["onion", "no", "on", "ion"],
    hp: 2,
    color: Colors.SEA_GREEN_4,
  }),
  ogre: buildDefaultEnemy({
    initial: "ogre",
    words: ["overwhelming", "ogre"],
    hp: 2,
    color: Colors.SEA_GREEN_4,
  }),
  dragon: buildDefaultEnemy({
    initial: "dragon",
    words: ["dragon", "dangerous", "fiery", "greedy"],
    hp: 4,
    color: Colors.SEA_GREEN_4,
  }),
  poppy: buildDefaultEnemy({
    initial: "poppy",
    words: ["poppy", "peppy", "yappy"],
    hp: 2,
    color: Colors.SEA_GREEN_4,
  }),
  philospher: buildDefaultEnemy({
    initial: "philosopher",
    words: ["philosopher", "physicophilosophy", "ponderous"],
    hp: 2,
    color: Colors.SEA_GREEN_4,
  }),
  mote: buildDefaultEnemy({
    initial: "mote",
    words: [
      "invincible",
      "fearless",
      "sensual",
      "mysterious",
      "enchanting",
      "vigorous",
      "diligent",
      "overwhelming",
      "gorgeous",
      "passionate",
      "terrifying",
      "beautiful",
      "powerful",
      "mote",
    ],
    color: Colors.ARMY,
    hp: 5,
    triggers: { health: [["victoryHealth"]], hunger: [["standardHunger"]] },
  }),
} as const;

export const BreedTypes = Object.entries(breeds).reduce((r, [key, value]) => {
  r[key as keyof typeof breeds] = new ActorBreed(key, value);
  return r;
}, {} as Record<keyof typeof breeds, ActorBreed>);
